import {Vnode, VnodeDOM} from 'mithril';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import ItemList from 'flarum/common/utils/ItemList';
import extractText from 'flarum/common/utils/extractText';
import listItems from 'flarum/common/helpers/listItems';
import AbstractProcessingPage from './AbstractProcessingPage';
import FieldList from '../fields/FieldList';
import SaveControls from '../components/SaveControls';
import ValueHistory from '../utils/ValueHistory';
import PageContainer from '../components/PageContainer';
import ErrorPage from '../components/ErrorPage';
import Uploader from '../utils/Uploader';
import Submission from '../models/Submission';
import {formulaireSubmissionIndex} from '../routes';
import isHorizontalLayout from '../utils/isHorizontalLayout';

const translationPrefix = 'kilowhat-formulaire.forum.submission.';

export default class SubmissionPage extends AbstractProcessingPage {
    locking: boolean = false
    restoring: boolean = false
    deleting: boolean = false
    submission: Submission | null = null
    uploader!: Uploader
    history!: ValueHistory

    oninit(vnode: Vnode) {
        super.oninit(vnode);

        this.errorPage = null;

        const id = m.route.param('id');
        const preloadedSubmission = app.preloadedApiDocument<Submission>();

        if (preloadedSubmission) {
            this.show(preloadedSubmission);
        } else {
            // Always retrieve a fresh copy on page access
            // This ensures the data is up to date and that all relations are loaded
            app.store.find<Submission>('formulaire/submissions', id, {}, {
                errorHandler: this.pageLoadErrorHandler.bind(this),
            }).then(submission => {
                this.show(submission);
                m.redraw();
            });
        }
    }

    onremove(vnode: VnodeDOM) {
        super.onremove(vnode);

        delete document.body.dataset.formulaireFormId;
        delete document.body.dataset.formulaireSubmissionId;
    }

    show(submission: Submission) {
        const form = submission.form();

        // This shouldn't happen, but we do it for TypeScript
        if (!form) {
            throw 'Missing form relationship';
        }

        this.submission = submission;
        this.uploader = new Uploader(form);
        this.history = new ValueHistory(submission.formData());

        // These data attributes aren't used by Formulaire, but are useful for user customization
        document.body.dataset.formulaireFormId = form.id();
        document.body.dataset.formulaireSubmissionId = submission.id();
    }

    view() {
        if (this.errorPage) {
            return m(PageContainer, {
                showSideNav: app.forum.attribute('formulaireShowSideNavOnSubmission'),
                narrow: true,
            }, m(ErrorPage, {
                error: this.errorPage,
            }));
        }

        if (!this.submission) {
            return LoadingIndicator.component({className: 'LoadingIndicator--block'});
        }

        const form = this.submission.form();

        // This shouldn't happen, but we do it for TypeScript
        if (!form) {
            throw 'Missing form relationship';
        }

        const list = new FieldList({
            configs: form.template()!,
            submission: this.history.value,
            uploader: this.uploader,
            onchange: () => {
                this.history.save();
                this.dirty = true;
            },
            disabled: this.loading || this.uploader.uploading,
            validationErrors: this.validationErrors,
        });

        const badges = this.submission.badges().toArray();

        const controls = this.controls(this.submission).toArray();

        return m(PageContainer, {
            showSideNav: app.forum.attribute('formulaireShowSideNavOnSubmission'),
            narrow: true,
        }, [
            m('h2', [
                form.title(),
                badges.length ? m('ul.FormulaireDataTable-badges.badges', listItems(badges)) : null,
            ]),
            controls.length ? m('.Form-group', controls) : null,
            this.submission.attribute('canEdit') ? null : m('p', app.translator.trans(translationPrefix + 'edit-forbidden')),
            m('div', {
                className: isHorizontalLayout(this.submission.attribute('canEdit') ? 'StandaloneEdit' : 'SubmissionView') ? 'Formulaire-horizontal-layout' : '',
            }, list.listView(!this.submission.attribute('canEdit'))),
            m(SaveControls, {
                dirty: this.dirty,
                loading: this.loading,
                savedAt: this.savedAt,
                inError: Object.keys(this.validationErrors).length,
                history: app.forum.attribute('formulaireShowHistoryControls') ? this.history : null,
                onsave: () => {
                    this.loading = true;

                    this.submission!.save({
                        data: this.history.value,
                    }, {
                        errorHandler: this.onerror.bind(this),
                    }).then(() => {
                        this.loading = false;
                        this.dirty = false;
                        this.savedAt = new Date();
                        this.validationErrors = {};
                        this.history.clear();
                        m.redraw();
                    });
                },
                floating: true,
            }),
        ]);
    }

    controls(submission: Submission) {
        const items = new ItemList();

        if (submission.attribute('canLock')) {
            items.add('lock', Button.component({
                className: 'Button',
                loading: this.locking,
                onclick: () => {
                    this.locking = true;

                    submission.save({
                        isLocked: !submission.isLocked(),
                    }).then(() => {
                        this.locking = false;
                        m.redraw();
                    }).catch(err => {
                        this.locking = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + (submission.isLocked() ? 'unlock' : 'lock'))));
        }

        if (submission.isHidden() && submission.attribute('canRestore')) {
            items.add('hide', Button.component({
                className: 'Button',
                loading: this.restoring,
                onclick: () => {
                    this.restoring = true;

                    submission.save({
                        isHidden: false,
                    }).then(() => {
                        this.restoring = false;
                        m.redraw();
                    }).catch(err => {
                        this.restoring = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'restore')));
        }

        if (!submission.isHidden() && submission.attribute('canHide')) {
            items.add('hide', Button.component({
                className: 'Button',
                loading: this.deleting,
                onclick: () => {
                    this.deleting = true;

                    submission.save({
                        isHidden: true,
                    }).then(() => {
                        this.deleting = false;
                        m.redraw();
                    }).catch(err => {
                        this.deleting = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'hide')));
        }

        if (submission.isHidden() && submission.attribute('canDelete')) {
            items.add('delete', Button.component({
                className: 'Button',
                loading: this.deleting,
                onclick: () => {
                    if (confirm(extractText(app.translator.trans(translationPrefix + 'delete-confirmation', {
                        date: submission.createdAt(),
                    })))) {
                        this.deleting = true;

                        submission.delete().then(() => {
                            this.deleting = false;
                            m.redraw();

                            const form = submission.form();

                            if (form) {
                                m.route.set(formulaireSubmissionIndex(form));
                            }
                        }).catch(err => {
                            this.deleting = false;
                            m.redraw();
                            throw err;
                        });
                    }
                },
            }, app.translator.trans(translationPrefix + 'delete')));
        }

        return items;
    }
}
